import { of, Subject } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';

import {
  Component,
  ChangeDetectionStrategy,
  Input,
  OnDestroy,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';

import { SnCamera, SnAuditOperation } from '@lib/api/entities';
import { SnPlayerState } from '@lib/player/common';

import { SnCameraStreamViewComponent } from '../../components/camera-stream-view/camera-stream-view.component';
import { SnCameraAuditService } from '../../services/camera-audit.service';

@Component({
  selector: 'sn-audit-camera-stream-view',
  templateUrl: './audit-camera-stream-view.container.html',
  styleUrls: ['./audit-camera-stream-view.container.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnAuditCameraStreamViewContainer implements OnDestroy {
  public stopped: boolean = true;

  @ViewChild('cameraStreamView') public stream: SnCameraStreamViewComponent;

  @Input()
  public camera: SnCamera;

  @Input()
  public canEdit = false;

  @Input()
  public autoplay = true;

  @Input()
  public controls = true;

  @Input()
  public timestamp = true;

  @Input()
  public camTime: any;

  @Input()
  public allowTimeout = true;

  @Input()
  public actions = true;

  @Input()
  public alwaysOn = false;

  @Input()
  public pauseOnTimeChange = false;

  @Output() public readonly playerInit = new EventEmitter<void>();
  @Output() public readonly stateChange = new EventEmitter<SnPlayerState>();
  @Output() public readonly selectedTime = new EventEmitter<any>();

  private readonly _destroy$ = new Subject<void>();
  private readonly _played$ = new Subject<void>();

  constructor(private readonly _cameraAuditService: SnCameraAuditService) {}

  public ngOnDestroy(): void {
    this._stop();
    this._destroy$.next();
    this._destroy$.complete();
  }

  public onCameraStreamPlay(live: boolean): void {
    this._played$.next();
    this.playerInit.next();
    if (!this.stopped) {
      return;
    }
    this.stopped = false;
    this._destroy$.next();
    const auditOperation = live ? SnAuditOperation.ViewLive : SnAuditOperation.ViewRecord;

    this._cameraAuditService
      .startPlaying(this.camera, auditOperation)
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  public onCameraStreamStop(): void {
    of(null)
      .pipe(delay(3000), takeUntil(this._played$), takeUntil(this._destroy$))
      .subscribe(() => {
        this._stop();
      });
  }

  public onStateChange(state: SnPlayerState): void {
    this.stateChange.next(state);
  }

  public toggleFullscreen(): void {
    this.stream.toggleFullscreen(true);
  }

  private _stop(): void {
    this.stopped = true;
    this._cameraAuditService.stopPlaying(this.camera.id).subscribe(() => this._destroy$.next());
  }
}
